import { toast } from 'sonner'

/**
 * Read-only gating for the `annotator` role (P&ID annotation-only access).
 *
 * The backend `AnnotatorPolicyGuard` is the real enforcement (it 403s any
 * mutation outside the P&ID annotation allowlist). This module is the frontend
 * counterpart:
 *   (a) `isAnnotator` — the role check that disables write affordances up front
 *       (upload / chat / create-board / workflow), so annotators rarely hit the wall;
 *   (b) `notifyAnnotatorBlocked` — one friendly, debounced toast when the backend
 *       blocks a write (called from the API layer on the guard's 403).
 *
 * NOTE: an earlier version patched sonner's `toast.error` singleton at import
 * time to also swallow a feature's own stacked "Failed to X" toast. That global
 * mutation broke unrelated test suites (partial `sonner` mocks / spies) and was
 * removed. Up-front affordance gating is the primary UX; the few callers that
 * would still stack a generic error suppress it locally (see boardSlice).
 */

// Single source of truth for the role check (mirrors backend deriveOrgRole).
export { isAnnotatorRole as isAnnotator } from '@/types/users'

export const ANNOTATOR_READONLY_MESSAGE =
  'Your access is limited to viewing and annotating P&IDs.'

// Debounce so a burst of blocked writes shows the friendly toast at most once.
const TOAST_DEBOUNCE_MS = 3000
let lastToastAt = 0

/**
 * Show a single, debounced toast when the backend blocks an annotator write (403).
 */
export function notifyAnnotatorBlocked(message?: string): void {
  const now = Date.now()
  if (now - lastToastAt >= TOAST_DEBOUNCE_MS) {
    lastToastAt = now
    toast.error(message?.trim() || ANNOTATOR_READONLY_MESSAGE)
  }
}
